import { useRecoilState } from 'recoil';
import { searchPageState } from '../store/recoil/product-search';
import { useCallback, useEffect } from 'react';
import { useUrlInformation } from '../lib/url-parser';
import { useCategoriesContext } from '../store/categories.context';
import { useRouter } from 'next/router';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export const useSearchPageNumber = () => {
  const { originalCategory, query } = useUrlInformation();
  const { rootCategory } = useCategoriesContext();
  const [searchPage, setSearchPage] = useRecoilState(searchPageState);
  const router = useRouter();

  /**
   * Returns the URL to the category page with the specified page number.
   */
  const getPageUrl = useCallback((page: number): URL => {
    const path = originalCategory?.relativeUrl ?? rootCategory?.relativeUrl ?? '';
    const url = new URL(path, publicRuntimeConfig.appBaseUrl);

    url.searchParams.set('page', page.toString());

    if (query?.q) {
      url.searchParams.set('q', query.q);
    }

    return url;
  }, [originalCategory, query, rootCategory]);

  /**
   * Returns the URL to the next page of the search results.
   */
  const getNextPageUrl = useCallback((): URL => {
    return getPageUrl(searchPage + 1);
  }, [searchPage]);

  /**
   * Sets the page number in the URL and updates the router state.
   * If the page number is 1 and no page query parameter is present, it does nothing.
   */
  const setPageNumberToUrl = useCallback((pageNumber?: number): void => {
    let url = getNextPageUrl();

    if (pageNumber) {
      url = getPageUrl(pageNumber);
    }

    if (1 === pageNumber && !router?.query?.page) {
      return ;
    }

    router.push(url.toString(), undefined, { shallow: true });
  }, [originalCategory, query, rootCategory]);

  /**
   * Updates the page number in the state and URL when the component mounts.
   * If the page number is not set, it defaults to 1.
   */
  useEffect(() => {
    setPageNumberToUrl(searchPage);
  }, [searchPage]);

  /**
   * Increments the page number by 1 and updates the URL.
   */
  const incrementPageNumber = (): void => {
    setSearchPage((prev) => prev + 1);
  }

  /**
   * Resets the page number to 1 and updates the URL.
   */
  const resetPageNumber = (): void => {
    setSearchPage(1);
  }

  return {
    incrementPageNumber,
    resetPageNumber,
    getNextPageUrl,
  }
}
